import { useTranslation } from "react-i18next";
import { Typography, Grid, Chip } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { MissionEnumsLists } from "../../../../common/constants";
import { GetTaskDriverState } from "../../../../common/mission";
import { NavLink } from "react-router-dom"
import { FormatLocation } from "../../../../common/maphelpers";
import { CompactTimeView, FullTimeView } from "../../../common/core/Time";
import TaskStateChip from "../../../common/display/TaskStateChip";
import { formatText } from "../../../../common/helpers";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import moment from "moment/moment";

const useStyles = makeStyles()(
  (theme) => ({
    link: {
      textDecoration: "none",
      color: "inherit"
    },
    slimRoot: {
      position: "relative"
    },
    root: {
      position: "relative",
      cursor: "pointer",
      "&:after": {
        zIndex: 3,
        position: "absolute",
        height: "calc(100% + 16px)",
        width: "calc(100% + 16px)",
        top: "-8px",
        left: "-8px",
        opacity: 0,
        background: theme.palette.action.hover,
        borderRadius: theme.spacing(1),
        content: "''",
        display: "block",
        pointerEvents: "none"
      },
      "&:hover:after": {
        opacity: 1
      }
    },
    highlight: {
      "&:after": {
        opacity: 1,
        border: `1px solid ${theme.palette.divider}`
      }
    },
    description: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflowX: "hidden"
    },
    timeLabel: {
      color: theme.palette.text.secondary,
      fontSize: "12px"
    },
    chipContainer: {
      marginTop: theme.spacing(-0.25),
      marginLeft: theme.spacing(-1.25),
      minWidth: "20px"
    },
    stateChip: {
      borderRadius: theme.spacing(1),
      fontSize: "14px"
    }
  })
);

export default function TaskItem(props) {
  const { missionTask, slim, highlight, onMouseEnter, onMouseLeave, onClick } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const eta = missionTask?.execution?.estimatedArrival;
  const targetType = missionTask.missionTarget.type;
  const targetDetails = targetType === 1 ? missionTask.missionTarget.carDetails : (targetType === 2 ? missionTask.missionTarget.boatDetails : null);
  const driverAssignedState = missionTask.execution.driverAssignedState;

  const location = missionTask.route?.waypoints[0];
  const formattedLocation = FormatLocation(location);
  const formattedDetails = missionTask.missionDetails?.description;
  const formattedDriver = formatText(" - ", missionTask.execution?.driver?.name, missionTask.execution?.vehicle?.name);

  const formattedRegistrationAndCause = formatText(" - ", targetDetails?.registration, missionTask?.missionDetails?.cause?.name);
  const formattedTarget = formatText(" ", targetDetails?.make, targetDetails?.model) || t(`mission:enums:targetType:${MissionEnumsLists.TargetType[targetType]}`);

  return (
    <Grid item xs={12}>
      <NavLink to={`./${missionTask.id}`} className={classes.link}>
        <Grid container className={`${slim ? classes.slimRoot : classes.root} ${highlight ? classes.highlight : ""}`} direction="row" alignItems="flex-end" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick}>

          <Grid item xs={12} container alignItems="baseline" spacing={1}>

            <Grid item xs>
              <Typography variant="subtitle1" lineHeight="130%">
                {t(`mission:enums:type:${MissionEnumsLists.Type[missionTask.missionDetails.type]}`)} - {formattedTarget}
              </Typography>
            </Grid>

            <Grid item>
              <Grid container>
                <TaskStateChip
                  size="small"
                  className={classes.stateChip}
                  state={missionTask.state}
                  driverAssignedState={driverAssignedState}
                  label={
                    <>
                      {GetTaskDriverState(driverAssignedState, missionTask.state, t, true)}
                      {!!missionTask.stateData?.lastModified ? <span className={classes.timeLabel}>&nbsp;(<CompactTimeView refresh={Date.now()} time={missionTask.stateData?.lastModified} />)</span> : null}
                    </>
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>

            {!!formattedLocation ? <Typography variant="body2" color="textSecondary" className={classes.description}>{formattedLocation}</Typography> : null}
            {!!formattedRegistrationAndCause ? <Typography variant="body2" color="textSecondary" className={classes.description}>{formattedRegistrationAndCause}</Typography> : null}
            {!!formattedDetails ? <Typography variant="body2" color="textSecondary" className={classes.description}>{formattedDetails}</Typography> : null}
            {!!eta ? <Typography variant="body2" color="textSecondary" className={classes.description}> ETA: <FullTimeView time={eta} /> ({moment(eta).fromNow()}) </Typography> : null}
            {!!formattedDriver ? <Typography variant="body2" color="textSecondary" className={classes.description}>{formattedDriver}</Typography> : null}

            {!formattedDriver && !formattedDetails && !formattedRegistrationAndCause && !formattedLocation ? (
              <Typography variant="body2" color="info.dark" className={classes.description}>
                <InfoIcon color="inherit" fontSize="inherit" sx={{ marginBottom: "-1.5px" }} /> {t("common:missingDetails")}
              </Typography>
            ) : null}

          </Grid>

          <Grid item container xs spacing={0.5} className={classes.chipContainer}>
            {missionTask?.missionDetails?.tags?.map((tag, i) => (
              <Grid item key={i}>
                <Chip label={tag.name} size={slim ? "small" : undefined} />
              </Grid>
            ))}
          </Grid>

          <Grid item >
            <Typography variant="body2" color="textDisabled">
              #{missionTask.missionNumber}.{missionTask.number} • <CompactTimeView refresh={Date.now()} time={missionTask.missionCreated} />
            </Typography>
          </Grid>

        </Grid>

      </NavLink>
    </Grid>
  );
}

